import React, {
  memo,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useSelector } from "react-redux";

import AddBuyerWayModal from "components/AuctionPenSaleView/AddBuyerWayModal";
import BuyerPicker from "components/AuctionPenSaleView/BuyerPicker";
import BuyerSearchModal from "components/AuctionPenSaleView/BuyerSearchModal";
import {
  KeypadInput,
  PricingSelect,
} from "components/AuctionPenSaleView/PricingInput";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import { WeighbridgeKeyboardSellForm } from "components/WeighbridgeKeyboardSellForm";

import { Digits, Key } from "constants/hotKey";
import { ApiModel } from "constants/loading";
import { Settings } from "constants/settings";

import WeighBridgeCurrentLotSummary from "containers/WeighbridgeSaleView/WeighbridgeCurrentLotSummary";

import { EMPTY_OBJECT, getUnitPriceString } from "lib";

import { getDefaultPropertyId } from "lib/properties";

import {
  getBusinessById,
  getCurrentSaleyard,
  getSaleLotById,
  getSetting,
  getVendorIdBySaleLotId,
} from "selectors";

import {
  WeighBridgeSaleDispatchContext,
  WeighBridgeSaleStateContext,
} from "./WeighBridgeSaleContext";

function CurrentLotAuctionViewComponent(props) {
  const {
    isSellingDetailsDirty,
    keyboardModeActive,
    saleLotId,
    sellingDetails,
  } = props;

  const { buyerId, propertyId, buyerWayId, price, pricingTypeId } =
    sellingDetails;

  const { updateSaleLotSellingDetails } = useContext(
    WeighBridgeSaleDispatchContext,
  );

  const saleLot = useSelector(getSaleLotById(saleLotId)) || EMPTY_OBJECT;
  const vendorId = useSelector(getVendorIdBySaleLotId(saleLotId));
  const buyer = useSelector(getBusinessById(buyerId)) || EMPTY_OBJECT;
  const saleyard = useSelector(getCurrentSaleyard);

  const [isBuyerPickerVisible, setIsBuyerPickerVisible] = useState(false);
  const [isCreateBuyerWayVisible, setIsCreateBuyerWayVisible] = useState(false);
  const [bidderFilterValue, setBidderFilterValue] = useState("");
  const [selectedInputEl, setSelectedInputEl] = useState(null);
  const [selectedInput, setSelectedInput] = useState(null);

  const prevSaleLotIdRef = useRef(saleLotId);
  const priceButtonRef = useRef(null);
  const priceModifiedRef = useRef(false);
  const needsPriceFocusRef = useRef(false);
  const saleLotIdsList = useMemo(() => [saleLotId], [saleLotId]);

  useEffect(() => {
    priceModifiedRef.current = false;
    needsPriceFocusRef.current = keyboardModeActive && Boolean(saleLotId);
  }, [keyboardModeActive, saleLotId]);

  // PricingSelect only mounts once pricingTypeId is set, so wait for that before focusing.
  const hasPricingTypeId = typeof pricingTypeId === "number";
  useEffect(() => {
    if (needsPriceFocusRef.current && hasPricingTypeId) {
      needsPriceFocusRef.current = false;
      priceButtonRef.current?.focus();
    }
  }, [saleLotId, keyboardModeActive, hasPricingTypeId]);

  // If the saleLot is updated in state for any reason (Eg we have saved the lot with a temp buyer way id and the
  // response contained a real id) update the form context.  This is not quite ideal because we COULD have a change in
  // the form that gets overridden because of pusher.
  const saleLotBuyerWayId = saleLot?.buyer_way?.id;
  useEffect(
    () =>
      updateSaleLotSellingDetails(
        {
          buyerWayId: saleLotBuyerWayId,
        },
        false,
      ),
    [saleLotBuyerWayId, updateSaleLotSellingDetails],
  );

  // Update (or clear) delivery property on buyer way (or buyer) change
  useEffect(() => {
    // buyerWayId also gets (re)synced here whenever the sale lot itself loads/changes
    // not just when the user picks a new buyer way.
    // If the buyer way id hasn't changed - no need to reset the property
    // An unsold lot's buyer way is null once loaded but undefined on the lot itself.
    if ((buyerWayId ?? null) === (saleLotBuyerWayId ?? null)) {
      return;
    }

    let defaultPropertyId;
    if (buyer?.buyerWays?.length && buyerWayId) {
      defaultPropertyId = getDefaultPropertyId(
        buyer,
        saleyard.id,
        buyer.buyerWays.find(buyerWay => buyerWay.id === buyerWayId)?.name,
      );
    }
    updateSaleLotSellingDetails({
      propertyId: defaultPropertyId,
    });
  }, [
    buyerWayId,
    updateSaleLotSellingDetails,
    buyer,
    saleyard,
    saleLotBuyerWayId,
  ]);

  useEffect(() => {
    // we want to ignore changes to the selected sale lot if the user has made changes,
    // unless they have changed Sale Lots, then we want to replace any entered values
    // with the ones from the selected Sale Lot.

    const hasSaleLotIdChanged = saleLotId !== prevSaleLotIdRef.current;
    const isFormClean = saleLotId && !isSellingDetailsDirty;
    if (hasSaleLotIdChanged && isFormClean) {
      const buyerWay = saleLot.buyer_way;
      updateSaleLotSellingDetails(
        {
          buyerId: saleLot.buyer_id,
          // using && allows the property to automatically fall back to null when there is not buyer way set
          buyerWayId: buyerWay && buyerWay.id,
          price: getUnitPriceString(saleLot),
          pricingTypeId: saleLot.pricing_type_id,
          propertyId: saleLot.destination_property_id,
        },
        false,
      );
    }

    // always make sure the most recent selected Sale Lot Id is stored
    prevSaleLotIdRef.current = saleLotId;
  }, [
    isSellingDetailsDirty,
    saleLotId,
    prevSaleLotIdRef,
    saleLot,
    updateSaleLotSellingDetails,
    buyerWayId,
  ]);

  useLayoutEffect(() => {
    function onKeyPress(e) {
      if (e.key !== Key.DELETE && e.key !== Key.ENTER) {
        if (selectedInput === KeypadInput.BIDDER_SEARCH) {
          setBidderFilterValue(bidderFilterValue + e.key);
        } else {
          const isPeriod = e.key === Key.PERIOD;
          if (Digits.includes(e.key) || isPeriod) {
            // First keypress on a pre-filled lot clears the existing price so the
            // user types a fresh value rather than appending to it. Subsequent
            // keypresses accumulate normally. The leading-zero strip prevents
            // "0150" after the clear; the duplicate-dot guard prevents "1..5".
            const basePrice =
              !priceModifiedRef.current && parseFloat(price) !== 0
                ? "0"
                : price;
            priceModifiedRef.current = true;
            const priceString =
              (!basePrice.includes(".") && isPeriod) || !isPeriod
                ? `${basePrice}${e.key}`
                : basePrice;
            updateSaleLotSellingDetails({
              price:
                priceString.charAt(0) === "0"
                  ? priceString.slice(1)
                  : priceString,
            });
          }
        }
      }
    }

    function onKeyUp(e) {
      if (e.key === "Backspace") {
        if (selectedInput === KeypadInput.BIDDER_SEARCH) {
          setBidderFilterValue(bidderFilterValue.slice(0, -1));
        } else {
          updateSaleLotSellingDetails({ price: price.slice(0, -1) || "0" });
        }
      }
    }

    if (selectedInputEl !== null) {
      selectedInputEl.addEventListener("keypress", onKeyPress);
      selectedInputEl.addEventListener("keyup", onKeyUp);
    }

    return () => {
      if (selectedInputEl !== null) {
        selectedInputEl.removeEventListener("keypress", onKeyPress);
        selectedInputEl.removeEventListener("keyup", onKeyUp);
      }
    };
  }, [
    selectedInput,
    selectedInputEl,
    bidderFilterValue,
    price,
    setBidderFilterValue,
    updateSaleLotSellingDetails,
  ]);

  function openAddBuyerWay() {
    setIsCreateBuyerWayVisible(true);
  }

  function closeAddBuyerWay() {
    setIsCreateBuyerWayVisible(false);
  }

  function closeSearch() {
    setIsBuyerPickerVisible(false);
  }

  function openSearch() {
    setIsBuyerPickerVisible(true);
  }

  function onBlurBidderSearch() {
    setSelectedInput(null);
  }

  function onFocusBidderSearch(e) {
    setSelectedInput(KeypadInput.BIDDER_SEARCH);
    setSelectedInputEl(e.currentTarget);
  }

  function onSelectBuyerWay(buyerWayId) {
    updateSaleLotSellingDetails({
      buyerWayId,
    });
  }

  function onSelectPricingTypeId(pricingTypeId) {
    updateSaleLotSellingDetails({
      pricingTypeId,
    });
  }

  function onSelectInput(inputName, e) {
    setSelectedInputEl(e.currentTarget);
  }

  if (!saleLotId) {
    return null;
  }

  return (
    <>
      <WeighBridgeCurrentLotSummary saleLotId={saleLotId} />
      {typeof pricingTypeId === "number" && (
        <PricingSelect
          isSingleLot
          price={price}
          priceButtonRef={priceButtonRef}
          selectedHdCount={saleLot.quantity}
          selectedInput={selectedInput}
          selectedPricingTypeId={pricingTypeId}
          setSelectedInput={onSelectInput}
          setSelectedPricingType={onSelectPricingTypeId}
          totalHdCount={saleLot.quantity}
        />
      )}
      {keyboardModeActive ? (
        <WeighbridgeKeyboardSellForm saleLotId={saleLotId} />
      ) : (
        <>
          <BuyerPicker
            bidderSearchValue={bidderFilterValue}
            buyerId={buyerId}
            buyerWayId={buyerWayId}
            isBidderSearchSelected={selectedInput === KeypadInput.BIDDER_SEARCH}
            onBlurBidderNumberSearch={onBlurBidderSearch}
            onFocusBidderNumberSearch={onFocusBidderSearch}
            onOpenAddBuyerWay={openAddBuyerWay}
            onSelectBuyerValues={updateSaleLotSellingDetails}
            openSearch={openSearch}
            propertyId={propertyId}
            saleLotIds={saleLotIdsList}
            vendorId={vendorId}
          />
          {isCreateBuyerWayVisible && (
            <AddBuyerWayModal
              buyerId={buyerId}
              setBuyerWayId={onSelectBuyerWay}
              closeSelf={closeAddBuyerWay}
            />
          )}
          {isBuyerPickerVisible && (
            <BuyerSearchModal
              setBuyerValues={updateSaleLotSellingDetails}
              closeSelf={closeSearch}
            />
          )}
        </>
      )}
    </>
  );
}

const CurrentLotAuctionView = memo(CurrentLotAuctionViewComponent);

function StateAdapter() {
  const { selectedSaleLotId, sellingDetails, isSellingDetailsDirty } =
    useContext(WeighBridgeSaleStateContext);

  const keyboardModeEnabled = useSelector(
    getSetting(Settings.weighbridgeKeyboardMode),
  );

  return (
    <WaitForSync requiredData={[ApiModel.CONSIGNMENTS, ApiModel.BUSINESSES]}>
      <CurrentLotAuctionView
        isSellingDetailsDirty={isSellingDetailsDirty}
        keyboardModeActive={!!keyboardModeEnabled}
        saleLotId={selectedSaleLotId}
        sellingDetails={sellingDetails}
      />
    </WaitForSync>
  );
}

export default memo(StateAdapter);
